import type { StaffCase } from '@fruuit/shared'
import {
  Badge,
  buttonClasses,
  Callout,
  Card,
  EmptyState,
  PageHeader,
  SkeletonText,
  StatusBadge,
} from '@fruuit/ui'
import { FileSearch, MessagesSquare, UsersRound } from 'lucide-react'
import { Link, useParams } from 'react-router'
import { useSession } from '../../auth/session-context'
import { StaffClientActionList } from '../../features/client-actions/ClientActionsPanel'
import {
  AUTHORING_PENDING,
  CreateDialogue,
  CreateMilestone,
  CreateObjective,
  EditEngagement,
} from '../../features/engagements/authoring'
import {
  CaseSummary,
  EngagementRecord,
  MeetingsCard,
  ObjectiveCard,
} from '../../features/engagements/CaseParts'
import {
  CaseHistory,
  ReleaseTag,
  ResponsivenessCard,
  releaseStatus,
  VisibilityTag,
} from '../../features/engagements/CaseRecord'
import { plural, shortDate, themeLabel } from '../../features/engagements/format'
import { useStaffCase } from '../../features/engagements/queries'
import { ParticipationCard } from '../../features/participation/ParticipationCard'
import { QuestionThread } from '../../features/questions/QuestionThread'
import { useTitle } from '../../lib/useTitle'

const meetingPath = (id: string) => `/workspace/meetings/${id}`

/** S5 Case, team variant (I-10, GAP-UI-012): every state, client questions and requests. */
export function CasePage() {
  const { id = '' } = useParams()
  const query = useStaffCase(id)
  useTitle(query.data?.issuer.name, 'Engagement', 'Workspace')

  if (query.isPending) {
    return (
      <div aria-busy="true" className="space-y-6">
        <SkeletonText lines={2} className="max-w-xl" />
        <SkeletonText lines={8} />
      </div>
    )
  }
  if (query.isError) {
    return query.error.code === 'not_found' ? (
      <EmptyState
        icon={<FileSearch />}
        title="This engagement is not available."
        action={
          <Link to="/workspace/engagements" className={buttonClasses({ variant: 'secondary' })}>
            Back to engagements
          </Link>
        }
      />
    ) : (
      <Callout tone="danger" role="alert">
        {query.error.message}
      </Callout>
    )
  }
  return <TeamCase data={query.data} refetch={() => void query.refetch()} />
}

function TeamCase({ data, refetch }: { data: StaffCase; refetch: () => void }) {
  const me = useSession()
  const openRequests = data.participation.filter((r) =>
    ['requested', 'acknowledged', 'company_consent_pending'].includes(r.status),
  )
  const awaiting = data.questions.filter((q) => q.status === 'open')

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Engagements', href: '/workspace/engagements' },
          { label: data.issuer.name },
        ]}
        eyebrow={`${themeLabel(data.theme)} · Team view`}
        title={data.issuer.name}
        description={data.title}
        actions={
          <EditEngagement
            engagementId={data.id}
            revision={data.revision}
            onSaved={refetch}
            size="md"
          />
        }
      />
      {data.released === null ? (
        <Callout tone="warning" className="-mt-4 mb-6" title="Not released to investors">
          Investors see nothing from this case until a reviewer releases it.
        </Callout>
      ) : null}
      {AUTHORING_PENDING ? (
        <p className="-mt-3 mb-5 text-xs text-sage">
          Editing and adding records opens with the records module; those buttons are marked until
          it is connected.
        </p>
      ) : null}

      <CaseSummary
        status={data.status}
        nextAction={data.next_action}
        extra={
          <>
            <StatusBadge status={data.state} />
            <ReleaseTag status={releaseStatus(data)} />
          </>
        }
        facts={[
          { label: 'Manager', value: data.manager.name },
          { label: 'Reviewer', value: data.reviewer.name },
          { label: 'Next deadline', value: shortDate(data.due_date) },
          { label: 'Fruuit coverage since', value: shortDate(data.fruuit_start) },
        ]}
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="min-w-0 space-y-6">
          <Card
            eyebrow="The change we seek"
            title="Objectives & progress"
            actions={<CreateObjective engagementId={data.id} onSaved={refetch} />}
          >
            {data.objectives.length === 0 ? (
              <EmptyState variant="plain" title="No objectives recorded yet." className="py-8" />
            ) : (
              data.objectives.map((objective) => (
                <ObjectiveCard
                  key={objective.id}
                  objective={objective}
                  caseTheme={data.theme}
                  actions={
                    <CreateMilestone
                      engagementId={data.id}
                      objectiveId={objective.id}
                      onSaved={refetch}
                    />
                  }
                />
              ))
            )}
          </Card>
          <EngagementRecord
            dialogues={data.dialogues}
            meetings={data.meetings}
            meetingPath={meetingPath}
            action={<CreateDialogue engagementId={data.id} onSaved={refetch} />}
          />
          <CaseHistory items={data.history} />
          <Card
            eyebrow="Client actions"
            title="What clients decided"
            actions={<Badge tone="outline">Client-private · read-only</Badge>}
          >
            <p className="mb-4 text-xs leading-relaxed text-sage">
              Recorded by investors for discussion. Read-only for Fruuit staff; each client sees
              only its own.
            </p>
            <StaffClientActionList items={data.client_actions} />
          </Card>
        </div>

        <aside className="min-w-0 space-y-6" aria-label="Release, clients and requests">
          <Card
            variant="tint"
            eyebrow="What investors see"
            padding="md"
            actions={<VisibilityTag released={data.released !== null} />}
          >
            {data.released ? (
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-xs text-sage">Released title</dt>
                  <dd className="font-medium text-ink">{data.released.title}</dd>
                </div>
                <div>
                  <dt className="text-xs text-sage">Released next action</dt>
                  <dd className="text-ink">
                    {data.released.next_action} · {shortDate(data.released.due_date)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-sage">Released</dt>
                  <dd className="text-ink">
                    {shortDate(data.released.released_at)}
                    {data.released.released_by ? ` by ${data.released.released_by.name}` : ''}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-sage">No released copy yet.</p>
            )}
            <div className="mt-4 border-t border-line pt-3">
              <p className="text-xs font-semibold text-teal-800">
                {data.client_org
                  ? `Client-specific: ${data.client_org.name}`
                  : `Subscribers (${data.subscribers.length})`}
              </p>
              {data.client_org ? null : data.subscribers.length === 0 ? (
                <p className="mt-1 text-sm text-sage">No client subscribes to this case yet.</p>
              ) : (
                <ul className="mt-1 space-y-0.5 text-sm text-ink">
                  {data.subscribers.map((s) => (
                    <li key={s.org_id}>
                      {s.org_name}{' '}
                      <span className="text-xs text-sage">since {shortDate(s.since)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          <ResponsivenessCard value={data.responsiveness} />
          <MeetingsCard meetings={data.meetings} meetingPath={meetingPath} />

          <Card
            title="Participation requests"
            actions={
              openRequests.length > 0 ? (
                <Badge tone="info">{openRequests.length} open</Badge>
              ) : undefined
            }
          >
            {data.participation.length === 0 ? (
              <EmptyState
                variant="plain"
                icon={<UsersRound />}
                title="No participation requests on this case."
                className="py-6"
              />
            ) : (
              <div className="space-y-4">
                {data.participation.map((r) => (
                  <ParticipationCard
                    key={r.id}
                    variant="staff"
                    request={r}
                    managers={data.managers}
                  />
                ))}
              </div>
            )}
          </Card>

          <Card
            title="Investor questions"
            actions={
              awaiting.length > 0 ? (
                <Badge tone="watch">
                  {plural(awaiting.length, 'awaiting reply', 'awaiting reply')}
                </Badge>
              ) : undefined
            }
          >
            {data.questions.length === 0 ? (
              <EmptyState
                variant="plain"
                icon={<MessagesSquare />}
                title="No questions on this case."
                className="py-6"
              />
            ) : (
              <div className="space-y-4">
                {data.questions.map((q) => (
                  <QuestionThread
                    key={q.id}
                    variant="staff"
                    question={q}
                    currentUserId={me.user.id}
                  />
                ))}
              </div>
            )}
          </Card>
        </aside>
      </div>
    </>
  )
}
